import ClassNames from "classnames";
import { Component, createRef, RefObject } from "react";
import { Transition } from "react-transition-group";
import styles from "../stylesheets/components/fixed-header.module.css";
import mainMenuStyles from "../stylesheets/components/main-menu.module.css";
import {
  Accommodation,
  ActionLinks,
  ColorScheme,
  Language,
  MainMenuItem,
  PageState,
  Picture,
  ThreeSizes,
} from "../types/index.js";
import { isElementPartiallyInViewport } from "../utils/utils.js";
import CompactHeaderBar from "./CompactHeaderBar.js";

interface Props {
  actionLinks: ActionLinks;
  activePagePath: string[];
  hasHeaderImage: boolean;
  isPreview: boolean;
  languageId: Language;
  pages: PageState;
  scheme: ColorScheme;
  topLevelMainMenuItems: MainMenuItem[];
  fallbackLanguageId: Language | undefined;
  pageId: string;
  logo: Picture;
  logoSize: ThreeSizes;
  mainPageURL: string | undefined;
  stickyEnabled: boolean;
  intersectionTriggerEl: HTMLElement | null;
  accommodation: Accommodation;
  isCircleHamburger: boolean;
  className?: string;
}

interface State {
  showFixedHeader: boolean;
}

class FixedHeader extends Component<Props, State> {
  private lastScrollTop = 0;
  private observer?: IntersectionObserver;
  private rafId: number | undefined;
  private nodeRef: RefObject<HTMLDivElement | null>;

  override state: State = { showFixedHeader: false };

  constructor(props: Props) {
    super(props);
    this.nodeRef = createRef<HTMLDivElement | null>();
    this.nodeRef.current = null;
  }

  override componentDidMount() {
    this.observer = new IntersectionObserver(this.handleIntersectionObserver);
    !this.props.isPreview &&
      window.addEventListener("scroll", this.onScrollRAF, { passive: true });
  }

  override componentDidUpdate(prevProps: Props) {
    const prevTriggerEl = prevProps.intersectionTriggerEl;
    const triggerEl = this.props.intersectionTriggerEl;
    if (prevTriggerEl !== triggerEl && this.observer && triggerEl) {
      prevTriggerEl && this.observer.unobserve(prevTriggerEl);
      this.observer.observe(triggerEl);
    }
  }

  handleIntersectionObserver = (entries: IntersectionObserverEntry[]) => {
    const [entry] = entries;
    if (!entry) return;

    const isSticky = !entry.isIntersecting;
    this.handleStickySwitch(isSticky);
  };

  handleFixedHeader = (isInViewport: boolean) => {
    const { showFixedHeader } = this.state;

    const scrollTop = window.scrollY;
    const hasScrolledUp = scrollTop < this.lastScrollTop;
    this.lastScrollTop = scrollTop;

    if (isInViewport && !showFixedHeader) return;

    if (isInViewport && showFixedHeader) {
      this.setState({ showFixedHeader: false });
      return;
    }

    hasScrolledUp &&
      !showFixedHeader &&
      this.setState({ showFixedHeader: true });

    !hasScrolledUp &&
      showFixedHeader &&
      this.setState({ showFixedHeader: false });
  };

  handleStickySwitch = (isSticky: boolean) => {
    const { stickyEnabled, isPreview } = this.props;
    if (!stickyEnabled || isPreview) return;

    this.setState({ showFixedHeader: isSticky });
  };

  onScroll = () => {
    const { stickyEnabled, intersectionTriggerEl } = this.props;

    // If sticky is set to true, it means that it always shows the
    // fixed header, not only on scroll up
    if (stickyEnabled || !intersectionTriggerEl) return;

    const isInViewport = isElementPartiallyInViewport(intersectionTriggerEl);
    this.handleFixedHeader(isInViewport);
  };

  onScrollRAF = () => {
    this.rafId = window.requestAnimationFrame(this.onScroll);
  };

  override componentWillUnmount() {
    this.rafId !== undefined && window.cancelAnimationFrame(this.rafId);
    this.observer && this.observer.disconnect();

    !this.props.isPreview &&
      window.removeEventListener("scroll", this.onScrollRAF);
  }

  override render() {
    const {
      actionLinks,
      activePagePath,
      hasHeaderImage,
      isPreview,
      languageId,
      pages,
      scheme,
      topLevelMainMenuItems,
      fallbackLanguageId,
      pageId,
      logo,
      logoSize,
      mainPageURL,
      accommodation,
      isCircleHamburger,
      className,
    } = this.props;

    return (
      <Transition
        in={this.state.showFixedHeader}
        timeout={250}
        mountOnEnter
        nodeRef={this.nodeRef}
      >
        {(state) => (
          <div
            ref={this.nodeRef}
            className={ClassNames(
              styles.HeaderModule__Sticky,
              className,
              {
                [styles[`HeaderModule__Sticky--exited`]]: state === "exited",
                [styles[`HeaderModule__Sticky--fixed`]]: !isPreview,
              },
              mainMenuStyles.MainMenu__HeaderModule__Sticky,
            )}
          >
            <CompactHeaderBar
              actionLinks={actionLinks}
              activePagePath={activePagePath}
              fallbackLanguageId={fallbackLanguageId}
              hasHeaderImage={hasHeaderImage}
              isPreview={isPreview}
              languageId={languageId}
              mainPageURL={mainPageURL}
              pageId={pageId}
              pages={pages}
              scheme={scheme}
              logo={logo}
              logoSize={logoSize}
              topLevelMainMenuItems={topLevelMainMenuItems}
              accommodation={accommodation}
              isCircleHamburger={isCircleHamburger}
              className={styles.HeaderModule__Sticky__CompactHeaderBar}
            />
          </div>
        )}
      </Transition>
    );
  }
}

export default FixedHeader;
